import React, { useState, useEffect } from 'react';
import { Globe, CheckCircle, Clock, ShoppingCart, Copy, RefreshCw, ChevronDown, ChevronUp, ExternalLink, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APP_HOST = process.env.REACT_APP_PRODUCTION_HOST
  || (process.env.REACT_APP_BACKEND_URL ? new URL(process.env.REACT_APP_BACKEND_URL).hostname : 'l-oeil-du-marchand.com');

const STATUS = {
  pending:  { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  bought:   { label: 'Acheté',     color: 'bg-blue-100 text-blue-700',   icon: ShoppingCart },
  active:   { label: 'Activé',     color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

function CopyBtn({ value, label }) {
  const copy = () => { navigator.clipboard.writeText(value); toast.success(`${label} copié !`); };
  return (
    <button onClick={copy} className="ml-1 p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title={`Copier ${label}`}>
      <Copy className="w-3.5 h-3.5" />
    </button>
  );
}

function DnsGuide({ req }) {
  const [open, setOpen] = useState(true);
  const subdomain = req.partner_subdomain || 'partenaire';
  const partnerUrl = `${window.location.origin}/p/${subdomain}`;

  const dnsRows = [
    { type: 'CNAME', name: '@',   value: APP_HOST, ttl: '10800', note: 'Domaine racine (ex: monsite.fr)' },
    { type: 'CNAME', name: 'www', value: APP_HOST, ttl: '10800', note: 'Sous-domaine www' },
  ];

  return (
    <div className="mt-3 border border-blue-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-blue-50 text-blue-800 font-semibold text-sm hover:bg-blue-100 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Globe className="w-4 h-4" />
          Guide étape par étape — {req.domain}
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="bg-white divide-y divide-gray-100">

          {/* Étape 1 */}
          <div className="px-4 py-3">
            <p className="font-semibold text-sm text-gray-800 mb-1">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-xs mr-2">1</span>
              Achetez le domaine sur Gandi
            </p>
            <a href="https://www.gandi.net/fr/domain" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm">
              <ExternalLink className="w-3.5 h-3.5" /> Ouvrir Gandi.net
            </a>
            <p className="text-xs text-gray-500 mt-1">
              Recherchez <strong>{req.domain}</strong> et achetez-le. Prix indicatif : <strong>{req.price_ttc ? `${req.price_ttc} €/an` : 'vérifier sur Gandi'}</strong>
            </p>
          </div>

          {/* Étape 2 */}
          <div className="px-4 py-3">
            <p className="font-semibold text-sm text-gray-800 mb-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-xs mr-2">2</span>
              Configurez le DNS sur Gandi (copier-coller exact)
            </p>
            <p className="text-xs text-gray-500 mb-2">
              Dans Gandi → domaine <strong>{req.domain}</strong> → Enregistrements DNS → Ajouter ces 2 enregistrements :
            </p>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2 font-semibold text-gray-600">Type</th>
                    <th className="text-left p-2 font-semibold text-gray-600">Nom</th>
                    <th className="text-left p-2 font-semibold text-gray-600">Valeur</th>
                    <th className="text-left p-2 font-semibold text-gray-600">TTL</th>
                  </tr>
                </thead>
                <tbody>
                  {dnsRows.map((row, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="p-2"><span className="font-mono font-bold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">{row.type}</span></td>
                      <td className="p-2">
                        <span className="font-mono font-bold text-gray-900">{row.name}</span>
                        <CopyBtn value={row.name} label="Nom" />
                      </td>
                      <td className="p-2">
                        <span className="font-mono text-blue-700 break-all">{row.value}</span>
                        <CopyBtn value={row.value} label="Valeur" />
                      </td>
                      <td className="p-2 text-gray-500">{row.ttl}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-1.5 italic">⏱ Propagation DNS : 1 à 24h maximum</p>
          </div>

          {/* Étape 3 */}
          <div className="px-4 py-3">
            <p className="font-semibold text-sm text-gray-800 mb-1">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-xs mr-2">3</span>
              Marquez comme "Acheté" ci-dessous
            </p>
            <p className="text-xs text-gray-500">Le partenaire recevra un email de confirmation automatique.</p>
          </div>

          {/* Étape 4 */}
          <div className="px-4 py-3 bg-emerald-50">
            <p className="font-semibold text-sm text-emerald-800 mb-1">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-600 text-white text-xs mr-2">4</span>
              Une fois le DNS propagé → marquez "Activé"
            </p>
            <p className="text-xs text-emerald-700">
              Le partenaire recevra un email d'activation. Sa page <strong>/p/{subdomain}</strong> sera accessible via <strong>{req.domain}</strong>.
            </p>
            <p className="text-xs text-emerald-600 mt-1">
              URL actuelle partenaire :
              <a href={partnerUrl} target="_blank" rel="noopener noreferrer" className="ml-1 underline">{partnerUrl}</a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DomainRequestsTab({ token }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/admin/domain-requests`, { headers });
      setRequests(res.data || []);
    } catch { toast.error('Erreur chargement demandes'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRequests(); }, []);

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await axios.patch(`${API}/admin/domain-requests/${id}`, { status }, { headers });
      toast.success(status === 'bought' ? 'Marqué comme acheté ✓' : 'Domaine activé ! Email envoyé au partenaire ✓');
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status, updated_at: new Date().toISOString() } : r));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setUpdatingId(null); }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm('Supprimer définitivement cette demande de domaine ?')) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/domain-requests/${id}`, { headers });
      toast.success('Demande supprimée');
      setRequests(prev => prev.filter(r => r.id !== id));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setDeletingId(null); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  if (loading) return <div className="flex justify-center py-16"><RefreshCw className="w-6 h-6 animate-spin text-blue-500" /></div>;

  const pending = requests.filter(r => r.status === 'pending');
  const bought  = requests.filter(r => r.status === 'bought');
  const active  = requests.filter(r => r.status === 'active');

  return (
    <div className="space-y-6" data-testid="domain-requests-tab">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'En attente', count: pending.length, color: 'bg-amber-50 border-amber-200 text-amber-700' },
          { label: 'Acheté',     count: bought.length,  color: 'bg-blue-50 border-blue-200 text-blue-700' },
          { label: 'Activé',     count: active.length,  color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl border p-4 text-center ${s.color}`}>
            <p className="text-2xl font-bold">{s.count}</p>
            <p className="text-sm font-medium">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Liste */}
      {requests.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-gray-400">
            <Globe className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="font-medium">Aucune demande de domaine</p>
            <p className="text-sm mt-1">Les demandes des partenaires apparaîtront ici.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map(req => {
            const st = STATUS[req.status] || STATUS.pending;
            const Icon = st.icon;
            return (
              <Card key={req.id} className="overflow-hidden">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Globe className="w-5 h-5 text-blue-600" />
                        <span className="font-bold text-lg text-gray-900">{req.domain}</span>
                        <Badge className={st.color}>
                          <Icon className="w-3 h-3 mr-1" />{st.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600">
                        <strong>{req.partner_name}</strong> — {req.partner_email}
                        {req.partner_subdomain && <span className="ml-2 text-xs text-gray-400">(/p/{req.partner_subdomain})</span>}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Demande reçue le {formatDate(req.created_at)}
                        {req.price_ttc && <span className="ml-2 font-medium text-gray-600">{req.price_ttc} {req.currency}/an</span>}
                      </p>
                    </div>
                    {/* Boutons d'action */}
                    <div className="flex items-center gap-2 shrink-0">
                      {req.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => updateStatus(req.id, 'bought')}
                          disabled={updatingId === req.id}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid={`domain-mark-bought-${req.id}`}
                        >
                          <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />
                          {updatingId === req.id ? '...' : 'Marquer acheté'}
                        </Button>
                      )}
                      {req.status === 'bought' && (
                        <Button
                          size="sm"
                          onClick={() => updateStatus(req.id, 'active')}
                          disabled={updatingId === req.id}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`domain-mark-active-${req.id}`}
                        >
                          <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                          {updatingId === req.id ? '...' : 'Activer'}
                        </Button>
                      )}
                      {req.status === 'active' && (
                        <a href={`https://${req.domain}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-300">
                            <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Voir le site
                          </Button>
                        </a>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteRequest(req.id)}
                        disabled={deletingId === req.id}
                        className="text-red-500 hover:bg-red-50 hover:text-red-700"
                        data-testid={`domain-delete-${req.id}`}
                        title="Supprimer cette demande"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <DnsGuide req={req} />
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
